/**
 * ThreadView
 * One conversation: the message cards and an inline reply. An unrecognized ("Other senders")
 * thread leads with its two decisions — Link to tenant or vendor (the only filled button) and
 * Discard — above the note and the message (BR-114/BR-115); those never appear on a matched thread.
 */

import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { asIsoDateTimeString } from '@resida/shared';
import { deriveTimezone, formatTimestamp } from '../../utils/date';
import { downloadEmailAttachment, type ThreadSummary, type EmailMessage } from '../../lib/api';

export const THREAD_GONE_MESSAGE =
  'This thread was already linked or discarded, so nothing was changed. Refresh the list to see where it went.';

const MESSAGE_TIMESTAMP_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

interface ThreadViewProps {
  thread: ThreadSummary | undefined;
  messages: EmailMessage[];
  senderAddress: string;
  /** The thread was linked or discarded elsewhere (409): show the error and disable both decisions. */
  gone: boolean;
  onBack: () => void;
  onLink: () => void;
  onDiscard: () => void;
  onSendReply: (bodyText: string) => Promise<void>;
  onError: (message: string) => void;
  /** IANA timezone for rendering message timestamps. */
  timezone?: string;
}

export function ThreadView({
  thread,
  messages,
  senderAddress,
  gone,
  onBack,
  onLink,
  onDiscard,
  onSendReply,
  onError,
  timezone: timezoneProp,
}: ThreadViewProps): JSX.Element {
  const timezone = timezoneProp ?? deriveTimezone('');
  const [replyText, setReplyText] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const isUnmatched = thread?.matchState === 'UNMATCHED';

  const handleSend = async (): Promise<void> => {
    if (!replyText.trim()) return;
    try {
      setIsSendingReply(true);
      await onSendReply(replyText.trim());
      setReplyText('');
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleDownload = async (id: string, fileName: string): Promise<void> => {
    try {
      const response = await downloadEmailAttachment(id);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to download attachment');
    }
  };

  return (
    <div data-testid="thread-view">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 min-h-[44px] -ml-2 px-2 transition-colors"
        data-testid="back-button"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        {isUnmatched || gone ? 'Other senders' : 'Back to Inbox'}
      </button>

      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-foreground mb-1">
            {thread?.subject || '(No subject)'}
          </h2>
          {isUnmatched && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span className="truncate">
                From <span className="font-semibold text-foreground">{senderAddress}</span>
              </span>
              <Badge variant="warning" data-testid="thread-not-recognized">
                Not recognized
              </Badge>
              {thread.spamFlagged && (
                <Badge variant="warning" data-testid="thread-view-possible-spam">
                  Possible spam
                </Badge>
              )}
              {thread.couldNotVerify && (
                <Badge variant="warning" data-testid="thread-could-not-verify">
                  Could not verify sender
                </Badge>
              )}
            </div>
          )}
        </div>
        {isUnmatched && (
          <div className="flex flex-col gap-2 md:flex-row md:shrink-0">
            <Button onClick={onLink} disabled={gone} data-testid="link-thread-to-tenant-button" className="w-full md:w-auto">
              Link to tenant or vendor
            </Button>
            <Button
              variant="outline"
              onClick={onDiscard}
              disabled={gone}
              data-testid="discard-thread-button"
              className="w-full md:w-auto text-destructive border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
            >
              Discard
            </Button>
          </div>
        )}
      </div>

      {gone && (
        <div
          role="alert"
          className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex flex-wrap items-center justify-between gap-3"
          data-testid="thread-gone-alert"
        >
          <span>{THREAD_GONE_MESSAGE}</span>
          <Button variant="outline" onClick={onBack} data-testid="thread-gone-back">
            Back to Other senders
          </Button>
        </div>
      )}

      {isUnmatched && (
        <div
          role="note"
          className="p-3 mb-4 bg-info/10 border border-info/30 rounded-md text-sm text-foreground"
          data-testid="unmatched-thread-note"
        >
          This sender isn&apos;t one of your tenants or vendors. Link it to file this thread under them, or
          discard it.
        </div>
      )}

      <div className="space-y-4 mb-6">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`rounded-lg border p-4 ${
              msg.direction === 'OUTBOUND'
                ? 'bg-primary/5 border-primary/20 md:ml-8'
                : 'bg-card border-border md:mr-8'
            }`}
            data-testid={`message-${msg.id}`}
          >
            <div className="flex justify-between items-center gap-3 mb-2 text-xs text-muted-foreground">
              <span
                className="font-medium min-w-0 truncate"
                title={msg.direction === 'OUTBOUND' ? 'You' : msg.fromAddress}
              >
                {msg.direction === 'OUTBOUND' ? 'You' : msg.fromAddress}
              </span>
              <span className="shrink-0">{formatTimestamp(asIsoDateTimeString(msg.createdAt), timezone, MESSAGE_TIMESTAMP_OPTIONS)}</span>
            </div>
            <div className="text-sm text-foreground whitespace-pre-wrap">{msg.bodyText}</div>
            {msg.attachments.length > 0 && (
              <div className="mt-3 space-y-1">
                {msg.attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between p-2 border border-border rounded-md text-xs"
                  >
                    <span className="text-foreground">
                      {attachment.fileName} ({Math.round(attachment.fileSize / 1024)} KB)
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => void handleDownload(attachment.id, attachment.fileName)}
                      data-testid={`download-attachment-${attachment.id}`}
                    >
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border border-border rounded-lg p-4 bg-card">
        <textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Write a reply..."
          rows={4}
          aria-label="Reply message"
          className="w-full p-3 border border-input rounded-md text-sm font-[inherit] resize-y outline-none bg-background text-foreground mb-3"
          data-testid="reply-textarea"
        />
        <div className="flex items-center gap-3">
          <span className="flex-1 text-xs text-muted-foreground">
            {isUnmatched ? 'Replying keeps this thread in Other senders until you link it.' : ''}
          </span>
          <Button
            variant={isUnmatched ? 'outline' : 'default'}
            onClick={() => void handleSend()}
            disabled={isSendingReply || !replyText.trim()}
            data-testid="send-reply-button"
          >
            {isSendingReply ? 'Sending...' : 'Send Reply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
